import React, { useState, useEffect } from "react";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

export default function WaitlistModal({ product, onClose }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    const message = `WAITLIST: ${product.display_name}${
      form.notes ? `\n\nUse case / notes:\n${form.notes}` : ""
    }`;
    const { error } = await supabase.from("contacts").insert({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: null,
      company: form.company || null,
      message,
    });
    setLoading(false);
    if (error) {
      console.error("[waitlist] supabase insert failed:", error);
      toast.error(`Submit failed: ${error.message || "unknown"}`);
      return;
    }
    toast.success(`You're on the ${product.display_name} waitlist. We'll be in touch.`);
    onClose();
  };

  const inputCls =
    "w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/30 rounded-none";

  return (
    <div
      className="fixed inset-0 z-[9500] bg-[#0A0A0A]/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="waitlist-modal"
    >
      <div
        className="bg-white border-4 border-[#15803D] shadow-pixel max-w-lg w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="font-arcade text-base text-[#BE185D] uppercase tracking-widest">
              &gt; Waitlist
            </p>
            <h2 className="font-pixel text-lg text-[#0A0A0A] mt-2">
              {product.display_name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#0A0A0A]/60 hover:text-[#15803D] transition-colors p-1"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <p className="font-terminal text-xs text-[#0A0A0A]/65 leading-relaxed mb-6">
          {product.tagline}. Drop your details and we'll let you know the day it launches.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="waitlist-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-arcade text-xs text-[#0A0A0A]/70 uppercase block mb-1">
                First name <span className="text-[#15803D]">*</span>
              </label>
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                placeholder="Jane"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-arcade text-xs text-[#0A0A0A]/70 uppercase block mb-1">
                Last name <span className="text-[#15803D]">*</span>
              </label>
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                placeholder="Doe"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="font-arcade text-xs text-[#0A0A0A]/70 uppercase block mb-1">
              Work email <span className="text-[#15803D]">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jane@company.com"
              className={inputCls}
            />
          </div>

          <div>
            <label className="font-arcade text-xs text-[#0A0A0A]/70 uppercase block mb-1">
              Company
            </label>
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder="Acme Inc."
              className={inputCls}
            />
          </div>

          <div>
            <label className="font-arcade text-xs text-[#0A0A0A]/70 uppercase block mb-1">
              Use case / what you'd like to see
            </label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Optional - tells us how to prioritize you."
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#15803D] text-white font-arcade text-lg uppercase tracking-widest py-3 border-2 border-[#15803D] hover:bg-transparent hover:text-[#15803D] transition-colors duration-150 btn-press disabled:opacity-50"
          >
            {loading ? "JOINING..." : (<>JOIN WAITLIST <Send size={16} /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
